(function run() {
  'use strict';
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
    return;
  }
  const root = document.querySelector('[data-dwm]');
  if (!root || root.__dwm) return;
  root.__dwm = true;
  const q = (n) => root.querySelector(`[data-dwm-${n}]`);
  const params = new URLSearchParams(location.search);
  const CAPTURE = root.classList.contains('dwm--capture');
  const EDITOR = !!(window.Shopify && window.Shopify.designMode);
  const REDUCED = !CAPTURE && (EDITOR || matchMedia('(prefers-reduced-motion: reduce)').matches || !window.gsap);
  const stage = q('stage'), runway = q('runway'), canvas = q('vortex');
  const ext = q('exterior'), clip = q('clip'), cam = q('cam'), room = q('room'), backing = q('backing');
  const interior = q('interior'), lockScroll = q('lockscroll'), lockup = q('lockup'), markA = q('mark-a');
  const ambientArt = document.querySelector('[data-dws-ambient-art]');
  const bandCanvas = document.querySelector('[data-dws-vortex]');
  const bandContext = bandCanvas?.getContext('2d');
  const shop = bandCanvas?.closest('.dws');
  const AMBIENT_ENABLED = root.dataset.dwmAmbient === 'true' && !REDUCED && !CAPTURE;
  let stageVisible = false, shopVisible = false;
  let ambientElapsed = 0, ambientLast = null, ambientLastDraw = 0, ambientRaf = 0;
  let ambientCallbacks = 0;
  let ambientSpin = 0, ambientBob = 0;
  const ambientCosts = [];

  const LOCK = { aOverCap: 8.8409, capFrac: 103 / 796, aAspect: 1037.01 / 1172.31, aMarginOverW: 346.99 / 1037.01 };
  const DOOR = { l: 0.43, r: 0.89, t: 0.21, b: 0.895, seam: 0.652, cy: 0.56 };
  const EXT_ASPECT = 772 / 1150, INT_ASPECT = 1920 / 993, TALL_CROP = [0.27, 0.73], CONSOLE_X = 0.485;

  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const lerp = (a, b, t) => a + (b - a) * t;
  const sstep = (a, b, v) => { const t = clamp((v - a) / (b - a)); return t * t * (3 - 2 * t); };
  function bezier(x1, y1, x2, y2) {
    const cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx, cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;
    const sx = (t) => ((ax * t + bx) * t + cx) * t, sy = (t) => ((ay * t + by) * t + cy) * t;
    return (x) => {
      if (x <= 0) return 0; if (x >= 1) return 1;
      let t = x;
      for (let i = 0; i < 8; i++) { const d = sx(t) - x, dx = (3 * ax * t + 2 * bx) * t + cx; if (Math.abs(d) < 1e-5 || dx === 0) break; t -= d / dx; }
      return sy(clamp(t));
    };
  }
  const settle = bezier(0.16, 1, 0.3, 1), glide = bezier(0.45, 0, 0.2, 1), plunge = bezier(0.6, 0, 0.9, 0.45), emerge = bezier(0.62, 0, 0.18, 1);

  let dirty = false;
  const requestApply = () => { if (!dirty) { dirty = true; queueMicrotask(apply); } };
  function state(init) {
    const o = {}, v = { ...init };
    for (const k of Object.keys(init)) Object.defineProperty(o, k, { enumerable: true, get: () => v[k], set: (x) => { if (v[k] !== x) { v[k] = x; requestApply(); } } });
    return o;
  }
  const L_END = { v: 0.4, travel: 3.0, twist: 0.45, spin: 0.12, core: 0, arrive: 1, enter: 1, room: 1, dw: 1 };
  const S_END = { cam: 1, lift: 1, settle: 1, sky: 1 };
  const L = state({ v: 1, travel: 0, twist: 1, spin: 0, core: 1, arrive: 0, enter: 0, room: 0, dw: 0 });
  const S = state({ cam: 0, lift: 0, settle: 0, sky: 0 });

  const G = {};
  function layout() {
    const W = stage.clientWidth, H = stage.clientHeight;
    G.W = W; G.H = H;
    G.top = parseFloat(getComputedStyle(stage).top) || 0;
    const tall = /interior-tall/.test(interior.currentSrc || '');
    let Hc = Math.max(0.8 * H, (1.04 * W) / INT_ASPECT);
    if (tall) Hc = Math.max(Hc, W / 0.39 / INT_ASPECT);
    const Wc = Hc * INT_ASPECT, landscape = W / H >= 1;
    const left = W / 2 - CONSOLE_X * Wc, top = H - Hc + (landscape ? 0.10 : 0.03) * H;
    const ix = tall ? left + TALL_CROP[0] * Wc : left, iw = tall ? (TALL_CROP[1] - TALL_CROP[0]) * Wc : Wc;
    Object.assign(interior.style, { left: ix + 'px', top: top + 'px', width: iw + 'px', height: Hc + 'px' });
    G.cam = { x: W / 2, y: top + 0.58 * Hc };
    cam.style.transformOrigin = room.style.transformOrigin = `${G.cam.x}px ${G.cam.y}px`;

    const hh = Math.min(0.36 * W, 0.17 * H, 200);
    const cap = LOCK.capFrac * hh, aH = LOCK.aOverCap * cap;
    const lk = { cap, aH, aW: aH * LOCK.aAspect };
    lk.aMargin = LOCK.aMarginOverW * lk.aW;
    lk.y0 = 0.40 * H - aH / 2;
    lk.k1 = landscape ? 0.74 : 0.78;
    Object.assign(markA.style, { width: lk.aW + 'px', height: aH + 'px' });
    lockScroll.style.top = lk.y0 + 'px';

    lk.y1 = Math.max(0.065 * H, lk.aMargin * lk.k1);
    G.lk = lk;

    const hT = Math.min(0.62 * H, (0.8 * W) / EXT_ASPECT), wT = hT * EXT_ASPECT;
    G.ext = { w: wT, h: hT, ox: DOOR.seam * wT, oy: DOOR.cy * hT };
    ext.style.width = wT + 'px';
    ext.style.transformOrigin = `${G.ext.ox}px ${G.ext.oy}px`;
    vortex.resize(W, H);
  }

  let frames = 0, maskStrength = 0;
  function apply() {
    dirty = false; frames++;
    const { W, H } = G;
    const a = L.arrive, e = L.enter, k = emerge(a);
    const sArr = lerp(0.03, 1, k);
    const rot = (lerp(-58, -5, k) + 6 * Math.sin(Math.PI * k) * (1 - k)) * (1 - sstep(0, 0.28, e));
    const s = sArr * (1 + 9 * plunge(e));
    const v0 = { x: W / 2, y: 0.46 * H }, vf = { x: W / 2 + (DOOR.seam - 0.5) * G.ext.w, y: 0.5 * H + (DOOR.cy - 0.5) * G.ext.h };
    const D = { x: lerp(v0.x, vf.x, k) + 0.07 * W * Math.sin(Math.PI * k) * (1 - k), y: lerp(v0.y, vf.y, k) };
    ext.style.opacity = a > 0 && e < 1 ? (sstep(0, 0.1, a) * (1 - sstep(0.72, 0.84, e))).toFixed(4) : '0';
    ext.style.transform = `translate(${(D.x - G.ext.ox).toFixed(2)}px,${(D.y - G.ext.oy).toFixed(2)}px) rotate(${rot.toFixed(3)}deg) scale(${s.toFixed(4)})`;

    const o = sstep(0.1, 0.42, e);
    if (e <= 0.1) clip.style.visibility = 'hidden';
    else {
      clip.style.visibility = 'visible';
      if (e >= 1) clip.style.clipPath = 'none';
      else {
        const { w, h, ox, oy } = G.ext;
        const xl = (DOOR.seam - o * (DOOR.seam - DOOR.l)) * w, xr = (DOOR.seam + o * (DOOR.r - DOOR.seam)) * w;
        const X = (x) => D.x + (x - ox) * s, Y = (y) => D.y + (y - oy) * s;
        const t = Math.max(0, Y(DOOR.t * h)), b = Math.max(0, H - Y(DOOR.b * h)), l = Math.max(0, X(xl)), r = Math.max(0, W - X(xr));
        clip.style.clipPath = `inset(${t.toFixed(1)}px ${r.toFixed(1)}px ${b.toFixed(1)}px ${l.toFixed(1)}px)`;
      }
    }
    backing.style.opacity = (1 - sstep(0.84, 1, e)).toFixed(4);
    room.style.transform = `scale(${lerp(0.86, 1, settle(L.room)).toFixed(4)})`;
    const c = glide(S.cam);
    cam.style.transform = `translateY(${(-0.03 * H * c).toFixed(2)}px) scale(${(1 + 0.14 * c + 0.04 * glide(S.settle)).toFixed(4)})`;

    const lk = G.lk, aT = settle(L.dw), aOp = sstep(0, 0.6, L.dw);
    lockup.style.visibility = L.dw > 0 ? 'visible' : 'hidden';
    markA.style.opacity = aOp.toFixed(4);
    markA.style.transform = `translateY(${(-2 * lk.cap * (1 - aT)).toFixed(2)}px)`;
    const lift = glide(S.lift), ks = lerp(1, lk.k1, lift), ly = (lk.y1 - lk.y0) * lift;
    lockScroll.style.transform = `translateY(${ly.toFixed(2)}px) scale(${ks.toFixed(4)})`;
    if (shop) {
      const markBox = markA.getBoundingClientRect();
      const shopBox = shop.getBoundingClientRect();
      const seam = Math.max(0, markBox.bottom - shopBox.top + markBox.width * LOCK.aMarginOverW + 2);
      shop.style.setProperty('--dw-band-seam', `${seam.toFixed(2)}px`);
    }


    const eye = { x: W / 2, y: lerp(0.46, -0.08, sstep(0, 1, L.room)) * H };
    const mx = W / 2, my = lk.y0 + ly + ks * (lk.aH / 2 - 2 * lk.cap * (1 - aT)), rx = 0.62 * ks * lk.aW + 0.5 * lk.aMargin, ry = 0.62 * ks * lk.aH + 0.5 * lk.aMargin;
    const dEye = Math.hypot((eye.x - mx) / rx, (eye.y - my) / ry);
    maskStrength = sstep(0, 0.2, L.dw) * (1 - sstep(1.0, 1.6, dEye));
    vortex.render({ cx: eye.x, cy: eye.y, travel: L.travel + 0.9 * S.sky + 0.15 * S.cam, twist: L.twist, spin: L.spin + 0.07 * S.sky + ambientSpin,
      gain: L.v * (1 - 0.25 * glide(S.settle)), core: L.core, mask: [mx, my, rx, ry], ms: maskStrength });
    if (bandContext && vortex.ok) {
      if (bandCanvas.width !== canvas.width || bandCanvas.height !== canvas.height) {
        bandCanvas.width = canvas.width;
        bandCanvas.height = canvas.height;
      }
      bandContext.drawImage(canvas, 0, 0);
    }
    if (ambientArt) {
      ambientArt.style.opacity = AMBIENT_ENABLED && L.room > .999 && S.lift > .999 ? (W < 768 ? '.22' : '.45') : '0';
      ambientArt.style.transform = `translateY(${ambientBob.toFixed(2)}px)`;
    }
    syncAmbient();
  }

  const ambientReady = () => {
    if (!AMBIENT_ENABLED || document.hidden || L.room < .999 || S.lift < .999) return false;
    const backdropVisible = stageVisible || shopVisible;
    if (!backdropVisible) return false;
    return true;
  };
  function syncAmbient() {
    if (ambientReady()) {
      if (!ambientRaf) ambientRaf = requestAnimationFrame(ambientTick);
    } else {
      if (ambientRaf) cancelAnimationFrame(ambientRaf);
      ambientRaf = 0;
      ambientLast = null;
    }
  }
  const backdropObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.target === stage) stageVisible = entry.isIntersecting;
      if (entry.target === shop) shopVisible = entry.isIntersecting;
    }
    syncAmbient();
  });
  backdropObserver.observe(stage);
  if (shop) backdropObserver.observe(shop);
  function ambientTick(ts) {
    ambientCallbacks++;
    ambientRaf = 0;
    if (!ambientReady()) { ambientLast = null; return; }
    if (ambientLast !== null) ambientElapsed += Math.min(ts - ambientLast, 50);
    ambientLast = ts;
    if (ts - ambientLastDraw >= 1000 / 30) {
      ambientLastDraw = ts;
      ambientSpin = ambientElapsed / 90000;
      ambientBob = 6 * Math.sin(2 * Math.PI * ambientElapsed / 6000);
      const start = performance.now();
      apply();
      ambientCosts.push(performance.now() - start);
      if (ambientCosts.length > 180) ambientCosts.shift();
    }
    if (!ambientRaf) ambientRaf = requestAnimationFrame(ambientTick);
  }
  document.addEventListener('visibilitychange', syncAmbient);

  const vortex = (() => {
    let gl = null, scale = 1, draws = 0;
    const uni = {};
    try { gl = canvas.getContext('webgl', { antialias: false, alpha: false, preserveDrawingBuffer: true, powerPreference: 'low-power' }); } catch (_) { gl = null; }
    if (!gl) return { ok: false, resize() {}, render() {}, hash: () => 'no-webgl', get draws() { return 0; } };
    const vs = 'attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}';
    const fs = `precision mediump float;
uniform vec2 uRes;uniform vec2 uC;uniform float uTravel,uTwist,uSpin,uGain,uCore,uMs;uniform vec4 uM;uniform sampler2D uN;
const vec3 SPACE=vec3(0.0,0.055,0.075);const vec3 TEAL=vec3(0.0,0.22,0.286);const vec3 TEALL=vec3(0.13,0.43,0.49);
const vec3 CREAM=vec3(0.941,0.894,0.698);const vec3 BURG=vec3(0.345,0.102,0.145);const vec3 GOLD=vec3(0.71,0.537,0.196);
void main(){
  vec2 q=(gl_FragCoord.xy-uC)/min(uRes.x,uRes.y);
  float r=length(q)+1e-4;float a=atan(q.y,q.x)*0.1591549;float z=0.28/r;
  float fog=smoothstep(0.02,0.55,r);
  float mk=1.0-uMs*(1.0-smoothstep(0.6,1.0,length((gl_FragCoord.xy-uM.xy)/uM.zw)));
  float ua=a+uTwist*0.045*z+uSpin;
  float nA=texture2D(uN,vec2(ua*4.0,z*0.18-uTravel)).r;
  float nA2=texture2D(uN,vec2(ua*9.0,z*0.40-uTravel*1.6)).g;
  float sA=smoothstep(0.52,0.96,nA*0.7+nA2*0.4)*mk;
  float ub=a-uTwist*0.035*z-uSpin*0.6+0.31;
  float nB=texture2D(uN,vec2(ub*3.0,z*0.15-uTravel*0.8)).b;
  float nB2=texture2D(uN,vec2(ub*7.0,z*0.33-uTravel*1.3)).r;
  float sB=smoothstep(0.56,0.97,nB*0.75+nB2*0.35)*mk;
  vec3 col=mix(SPACE,TEAL,smoothstep(1.15,0.0,r)*0.55);
  col+=BURG*sB*1.05*fog;col+=GOLD*pow(sB,2.6)*0.9*fog;
  col+=TEALL*sA*0.95*fog;col+=CREAM*pow(sA,2.8)*0.8*fog;
  float sp=texture2D(uN,vec2(a*36.0,z*1.1-uTravel*3.0)).a;
  col+=CREAM*smoothstep(0.9,1.0,sp)*fog*0.85*mk;
  col+=CREAM*uCore*(exp(-r*r*30.0)*1.15+exp(-r*5.5)*0.22)*mk;
  col=mix(SPACE,col,uGain);
  gl_FragColor=vec4(col,1.0);
}`;
    const sh = (t, src) => { const o = gl.createShader(t); gl.shaderSource(o, src); gl.compileShader(o); if (!gl.getShaderParameter(o, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(o)); return o; };
    const prog = gl.createProgram(); gl.attachShader(prog, sh(gl.VERTEX_SHADER, vs)); gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, fs)); gl.linkProgram(prog); gl.useProgram(prog);
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'p'); gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    for (const n of ['uRes', 'uC', 'uTravel', 'uTwist', 'uSpin', 'uGain', 'uCore', 'uMs', 'uM', 'uN']) uni[n] = gl.getUniformLocation(prog, n);
    const N = 256, data = new Uint8Array(N * N * 4);
    let seed = 1983;
    const rnd = () => { seed |= 0; seed = (seed + 0x6D2B79F5) | 0; let t = Math.imul(seed ^ (seed >>> 15), 1 | seed); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
    const lattice = (g) => { const a = new Float32Array(g * g); for (let i = 0; i < a.length; i++) a[i] = rnd(); return a; };
    const smp = (lat, g, x, y) => {
      const fx = x * g, fy = y * g, x0 = Math.floor(fx), y0 = Math.floor(fy), tx = fx - x0, ty = fy - y0, sx = tx * tx * (3 - 2 * tx), sy = ty * ty * (3 - 2 * ty);
      const i = (xx, yy) => lat[((yy % g + g) % g) * g + ((xx % g + g) % g)];
      return lerp(lerp(i(x0, y0), i(x0 + 1, y0), sx), lerp(i(x0, y0 + 1), i(x0 + 1, y0 + 1), sx), sy);
    };
    const oct = [[8, 0.62, 24, 0.38], [8, 0.6, 32, 0.4], [6, 0.66, 16, 0.34]];
    for (let c = 0; c < 3; c++) {
      const [g1, w1, g2, w2] = oct[c], l1 = lattice(g1), l2 = lattice(g2);
      for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) data[(y * N + x) * 4 + c] = 255 * (w1 * smp(l1, g1, x / N, y / N) + w2 * smp(l2, g2, x / N, y / N));
    }
    for (let i = 0; i < N * N; i++) data[i * 4 + 3] = 255 * Math.pow(rnd(), 14);
    gl.bindTexture(gl.TEXTURE_2D, gl.createTexture());
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, N, N, 0, gl.RGBA, gl.UNSIGNED_BYTE, data);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
    gl.uniform1i(uni.uN, 0);
    return {
      ok: true,
      resize(W, H) {
        scale = CAPTURE ? 1 : Math.min(window.devicePixelRatio || 1, 2) * 0.55;
        const w = Math.max(2, Math.round(W * scale)), h = Math.max(2, Math.round(H * scale));
        if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
        gl.viewport(0, 0, w, h);
      },
      render(p) {
        draws++;
        const ch = canvas.height;
        gl.uniform2f(uni.uRes, canvas.width, ch);
        gl.uniform2f(uni.uC, p.cx * scale, ch - p.cy * scale);
        gl.uniform1f(uni.uTravel, p.travel); gl.uniform1f(uni.uTwist, p.twist); gl.uniform1f(uni.uSpin, p.spin);
        gl.uniform1f(uni.uGain, p.gain); gl.uniform1f(uni.uCore, p.core);
        gl.uniform4f(uni.uM, p.mask[0] * scale, ch - p.mask[1] * scale, Math.max(1, p.mask[2] * scale), Math.max(1, p.mask[3] * scale));
        gl.uniform1f(uni.uMs, p.ms);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
      },
      hash() {
        const w = canvas.width, h = canvas.height, px = new Uint8Array(w * h * 4);
        gl.readPixels(0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, px);
        let x = 2166136261; for (let i = 0; i < px.length; i += 7) { x ^= px[i]; x = Math.imul(x, 16777619); }
        return (x >>> 0).toString(16) + '@' + w + 'x' + h;
      },
      get draws() { return draws; }
    };
  })();

  const finalStill = () => { Object.assign(L, L_END); Object.assign(S, S_END); runway.classList.add('dwm-runway--still'); layout(); apply(); };
  const hooks = (extra) => { window.__dw = Object.assign({ layout, L, S, G, setAmbientTime(ms) { ambientElapsed = ms; ambientSpin = ms / 90000; ambientBob = 6 * Math.sin(2 * Math.PI * ms / 6000); apply(); }, get frames() { return frames; }, get draws() { return vortex.draws; },
    get maskStrength() { return maskStrength; }, get ambientElapsed() { return ambientElapsed; }, get ambientCallbacks() { return ambientCallbacks; }, get ambientCosts() { return [...ambientCosts]; },
    canvasHash: () => vortex.hash(), reduced: REDUCED, capture: CAPTURE, webgl: vortex.ok }, extra); };

  root.classList.remove('dwm--motion');
  if (!window.gsap) { hooks({ LANDING_S: 0, seek() {}, scrollProgress: () => 1, landingTime: () => 0 }); finalStill(); window.__dwReady = true; return; }

  const landing = gsap.timeline({ paused: true, defaults: { ease: 'none' } });
  landing
    .to(L, { travel: L_END.travel, duration: 4.6, ease: 'power2.out' }, 0)
    .to(L, { spin: L_END.spin, duration: 4.6, ease: 'power1.inOut' }, 0)
    .to(L, { twist: L_END.twist, duration: 3.2, ease: 'power1.inOut' }, 0.6)
    .to(L, { core: 0.35, duration: 1.6, ease: 'power1.inOut' }, 0.6)
    .to(L, { core: 0, duration: 1.0, ease: 'power1.in' }, 2.2)
    .to(L, { arrive: 1, duration: 2.2 }, 0.5)
    .to(L, { enter: 1, duration: 0.9 }, 2.5)
    .to(L, { v: L_END.v, duration: 1.4, ease: 'power1.inOut' }, 2.2)
    .to(L, { room: 1, duration: 1.7 }, 2.6)
    .to(L, { dw: 1, duration: 1.3 }, 3.1);
  const LANDING_S = landing.duration();
  const scroll = gsap.timeline({ paused: true, defaults: { ease: 'none' } });
  scroll
    .to(S, { sky: 1, duration: 1 }, 0)
    .to(S, { cam: 1, duration: 0.32 }, 0)
    .to(S, { lift: 1, duration: 0.3 }, 0)
    .to(S, { settle: 1, duration: 0.24 }, 0.76);
  window.__timelines = { main: landing, scroll };
  function seek(t, p) { landing.time(clamp(t, 0, LANDING_S), true); scroll.progress(clamp(p), true); apply(); }
  hooks({ seek, LANDING_S, scrollProgress: () => scroll.progress(), landingTime: () => landing.time() });

  layout();
  interior.addEventListener('load', () => { layout(); apply(); });
  if (window.ResizeObserver) new ResizeObserver(() => { layout(); apply(); }).observe(stage);
  else addEventListener('resize', () => { layout(); apply(); });

  if (CAPTURE) seek(Number(params.get('t') ?? LANDING_S), Number(params.get('p') ?? 0));
  else if (REDUCED) { runway.classList.add('dwm-runway--still'); layout(); seek(LANDING_S, 1); }
  else {
    const progressFromScroll = () => {
      const r = runway.getBoundingClientRect(), max = r.height - stage.clientHeight;
      return max > 0 ? clamp((G.top - r.top) / max) : 0;
    };
    let queued = false;
    const readScroll = () => {
      queued = false;
      const p = progressFromScroll();
      if (p !== scroll.progress()) scroll.progress(p);
      if (landing.isActive() && p > 0.002) landing.timeScale(3);
      syncAmbient();
    };
    addEventListener('scroll', () => { if (!queued) { queued = true; requestAnimationFrame(readScroll); } }, { passive: true });
    const p0 = progressFromScroll();
    scroll.progress(p0, true);
    if (p0 > 0.002 || root.dataset.dwmDeepEntry === 'true') landing.progress(1, true);
    else {
      landing.eventCallback('onComplete', () => { window.__dw.landingEndedAt = performance.now(); });
      const art = [ext, interior, markA].map((im) => (im.decode ? im.decode() : Promise.resolve()).catch(() => {}));
      Promise.race([Promise.all(art), new Promise((r) => setTimeout(r, 1500))]).then(() => {
        window.__dw.landingStartedAt = performance.now();
        if (scroll.progress() > 0.002) landing.timeScale(3);
        landing.play(0);
      });
    }
    apply();
  }
  window.__dwReady = true;
})();
